// Wallpaper layer for the app shell. A single fixed element sits behind .rv-root;
// the chosen image/gradient/solid paints on its ::before (blurred independently of
// the dim scrim on ::after). The shell surfaces are turned transparent via the
// [data-wallpaper] attribute in theme.css so the layer shows through - on the
// start screen only ("start" scope) or behind the whole shell ("app" scope).
// Proxied page iframes always stay opaque, so a website never bleeds the wallpaper.
// Animated wallpapers (an uploaded GIF/MP4/WebM, or a video link) play in a muted <video> inside
// the layer (GIFs animate as the background image) and pause whenever nobody can see them: the
// browser tab is hidden, the start screen is covered by another tab (start scope), Lite mode is on,
// the user prefers reduced motion, or "Play animated wallpapers" is off; then a still frame shows.
import { getAll, subscribe } from "../shared/settings-store.js";
import { WALLPAPER_PRESET_MAP } from "../shared/settings-schema.js";
import { safeUrl } from "../shared/look-preview.js";
import { getWallpaperImage, subscribeWallpaperImage } from "../shared/wallpaper-image.js";
import { getMediaInfo, getMediaBlob, subscribeMedia, isVideoKind, isVideoUrl } from "../shared/wallpaper-media.js";
import { isPerf } from "./perf.js";

let layer = null;
// The custom image is a multi-MB data URL; cache it so settings changes (blur/dim
// drags fire applyWallpaper rapidly) don't re-parse it from localStorage each time.
// It only changes via the image store, which refreshes the cache below.
let cachedImage = null;
function customImage() {
	if (cachedImage === null) cachedImage = getWallpaperImage();
	return cachedImage;
}
let lastImageVal = null; // last value written to --wp-image (skip redundant big writes)

function ensureLayer() {
	if (layer) return layer;
	if (typeof document === "undefined" || !document.querySelector(".rv-root")) return null; // app shell only
	layer = document.createElement("div");
	layer.className = "rv-wallpaper";
	layer.setAttribute("aria-hidden", "true");
	document.body.insertBefore(layer, document.body.firstChild);
	return layer;
}

// Build a CSS background-image value for the active selection, or "" for none.
// Returns { image, solid } - `solid` is a flat color painted under the image.
function resolve(s) {
	const sel = s.wallpaper;
	if (!sel || sel === "none") return { image: "", solid: "" };
	if (sel === "solid") return { image: "", solid: s.wallpaperSolid || "#0a0a0a" };
	if (sel === "custom") {
		const data = customImage();
		return data ? { image: `url("${data}")`, solid: "" } : { image: "", solid: "" };
	}
	if (sel === "animated") {
		const info = getMediaInfo();
		if (!info) return { image: "", solid: "" };
		const url = mediaUrl(); // null until the blob has been read from IndexedDB
		const still = info.poster ? `url("${info.poster}")` : "";
		if (isVideoKind(info.kind)) return { image: still, solid: "", video: url || "" };
		// A GIF can't be paused, so it is swapped for its still frame whenever nobody would see it move.
		return { image: url && motionAllowed(s) && layerVisible(s) ? `url("${url}")` : still, solid: "" };
	}
	if (sel === "url") {
		const safe = safeUrl(s.wallpaperUrl);
		if (safe && isVideoUrl(safe)) return { image: "", solid: "#000", video: safe };
		return safe ? { image: `url("${safe}")`, solid: "" } : { image: "", solid: "" };
	}
	const preset = WALLPAPER_PRESET_MAP[sel];
	if (!preset) return { image: "", solid: "" };
	return preset.kind === "gradient" ? { image: preset.value, solid: "" } : { image: `url("${preset.value}")`, solid: "" };
}

// Only allow http(s) image URLs, then percent-encode the characters that could
// terminate the url("…") string and inject extra CSS - the double quote and the
// backslash (escape char), plus any whitespace/newlines. Parens are harmless
// inside the double-quoted form, so they're intentionally left as-is.
export { safeUrl }; // shared with the Looks thumbnails (shared/look-preview.js)

// --- animated wallpapers -------------------------------------------------
let mediaObjectUrl = null, mediaLoading = false, mediaAddedAt = 0;
function mediaUrl() {
	const info = getMediaInfo();
	if (!info) { dropMediaUrl(); return null; }
	if (mediaObjectUrl && mediaAddedAt === info.addedAt) return mediaObjectUrl;
	if (!mediaLoading) {
		mediaLoading = true;
		getMediaBlob().then((blob) => {
			mediaLoading = false;
			dropMediaUrl();
			if (blob) { mediaObjectUrl = URL.createObjectURL(blob); mediaAddedAt = info.addedAt; applyWallpaper(); }
		}, () => { mediaLoading = false; });
	}
	return null;
}
function dropMediaUrl() { if (mediaObjectUrl) { URL.revokeObjectURL(mediaObjectUrl); mediaObjectUrl = null; } }

const reducedMotion = typeof matchMedia === "function" ? matchMedia("(prefers-reduced-motion: reduce)") : null;
function motionAllowed(s) {
	return s.wallpaperAnimate !== false && !isPerf() && !(reducedMotion && reducedMotion.matches);
}
// Would anyone see the layer right now? (Start scope: only while the start screen shows.)
function layerVisible(s) {
	if (document.hidden) return false;
	if (s.wallpaperScope === "app") return true;
	const start = document.getElementById("rv-start");
	return !start || !start.hidden;
}
let video = null;
function syncVideo(src, s) {
	if (!src) { if (video) { video.pause(); video.removeAttribute("src"); video.load(); video.remove(); video = null; } return; }
	if (!video) {
		video = document.createElement("video");
		video.className = "rv-wallpaper-video";
		video.muted = true; video.loop = true; video.playsInline = true; video.autoplay = false;
		video.setAttribute("muted", ""); video.setAttribute("playsinline", ""); video.setAttribute("aria-hidden", "true");
		video.preload = "auto";
		video.addEventListener("error", () => { if (video) video.hidden = true; }); // a dead link falls back to the still frame
	}
	if (video.parentNode !== layer) layer.appendChild(video);
	if (video.getAttribute("src") !== src) { video.hidden = false; video.src = src; }
	const fit = s.wallpaperFit === "contain" ? "contain" : s.wallpaperFit === "center" ? "none" : "cover";
	video.style.objectFit = fit;
	const play = motionAllowed(s) && layerVisible(s);
	if (play) { const p = video.play(); if (p && p.catch) p.catch(() => {}); }
	else video.pause();
	// Paused on a still frame when motion is off: keep it visible (the poster shows underneath anyway).
}

export function applyWallpaper() {
	const s = getAll();
	const root = document.documentElement;
	const { image, solid, video: videoSrc } = resolve(s);
	// High Contrast shows no wallpaper (theme.css hides the layer too, whatever order the two apply in)
	const on = !!(image || solid || videoSrc) && root.dataset.theme !== "contrast";

	root.dataset.wallpaper = on ? "on" : "off";
	root.dataset.wpScope = s.wallpaperScope === "app" ? "app" : "start";

	if (!on) {
		// Inert: CSS hides the layer when data-wallpaper isn't "on"; also clear the
		// image + dim so no leftover scrim can paint and the decode is freed.
		if (layer) { layer.style.removeProperty("--wp-image"); layer.style.removeProperty("--wp-bg"); layer.style.setProperty("--wp-dim", "0"); lastImageVal = null; syncVideo(null, s); }
		if (s.wallpaper !== "animated") dropMediaUrl();
		return;
	}
	const el = ensureLayer();
	if (!el) return;
	syncVideo(videoSrc || null, s);
	if (s.wallpaper !== "animated") dropMediaUrl();

	const fit = s.wallpaperFit || "cover";
	const size = fit === "tile" ? "auto" : fit === "center" ? "auto" : fit; // cover|contain pass through
	const repeat = fit === "tile" ? "repeat" : "no-repeat";
	const blur = Math.max(0, Math.min(40, Number(s.wallpaperBlur) || 0));
	const dim = Math.max(0, Math.min(85, Number(s.wallpaperDim) || 0)) / 100;

	// --wp-image can be a multi-MB data URL; only write it when it actually changed
	// (a blur/dim drag re-runs this many times with the same image).
	const imageVal = image || "none";
	if (imageVal !== lastImageVal) { el.style.setProperty("--wp-image", imageVal); lastImageVal = imageVal; }
	el.style.setProperty("--wp-bg", solid || "transparent");
	el.style.setProperty("--wp-size", size);
	el.style.setProperty("--wp-repeat", repeat);
	el.style.setProperty("--wp-blur", blur + "px");
	if (video) video.style.filter = blur ? "blur(" + blur + "px)" : "";
	el.style.setProperty("--wp-dim", String(dim));
}

export function initWallpaper() {
	applyWallpaper();
	subscribe(applyWallpaper); // settings changes (local or from the settings tab)
	// The uploaded image changed (any tab) - refresh the cache, then re-apply.
	subscribeWallpaperImage(() => { cachedImage = getWallpaperImage(); applyWallpaper(); });
	subscribeMedia(() => applyWallpaper()); // an animated wallpaper was added/removed (any tab)
	// Pause/resume as visibility changes: browser tab, start screen covered, reduced motion.
	document.addEventListener("visibilitychange", applyWallpaper);
	if (reducedMotion && reducedMotion.addEventListener) reducedMotion.addEventListener("change", applyWallpaper);
	const start = document.getElementById("rv-start");
	if (start && typeof MutationObserver !== "undefined") new MutationObserver(() => { if (video || getAll().wallpaper === "animated") applyWallpaper(); }).observe(start, { attributes: true, attributeFilter: ["hidden"] });
}
